import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import config from "@config";
import axios from "@helpers/axios/private.axios";
import { useTranslation } from "@helpers/translate";
import { ALERT } from "@constants";
import {
  DEFAULT_SETTINGS,
  loadDrawingToolbarPosition,
  parseChartSettings,
  saveDrawingToolbarPosition,
} from "../settings/chartStorage";
import getEffectiveChartSettings from "../settings/effectiveChartSettings";

const CHART_SETTINGS_URL = "/futures/chart-settings";
const SAVE_DELAY_MS = 500;
const TOOLBAR_POSITION_FIELD = "drawingToolbarPosition";

const isSameValue = (first, second) =>
  JSON.stringify(first) === JSON.stringify(second);

/**
 * Chart settings saved on the user, one set for every pair. Nothing is sent to
 * the backend until they are loaded: the chart reports its list of indicators as
 * soon as it starts, an empty one must not replace the saved one. After a failed
 * load the chart runs with the defaults and saves nothing.
 *
 * @returns {{ settings: object, effectiveSettings: object, updateSettings: (partial: object) => void, isLoaded: boolean }}
 */
const useChartSettings = () => {
  const dispatch = useDispatch();
  const { __ } = useTranslation();
  const token = useSelector((state) => state.auth.token);
  const [settings, setSettings] = useState(() => ({
    ...DEFAULT_SETTINGS,
    [TOOLBAR_POSITION_FIELD]: loadDrawingToolbarPosition(),
  }));
  const [isLoaded, setIsLoaded] = useState(false);
  const settingsRef = useRef(settings);
  const canSaveRef = useRef(false);
  const pendingRef = useRef({});
  const timerRef = useRef(null);
  const showSaveErrorRef = useRef(null);

  showSaveErrorRef.current = () =>
    dispatch({ type: ALERT.ALERT_ERROR, payload: __("common.error") });

  const takePending = () => {
    const pending = pendingRef.current;

    clearTimeout(timerRef.current);
    pendingRef.current = {};

    return Object.keys(pending).length ? pending : null;
  };

  const sendPending = useCallback(() => {
    const pending = takePending();

    if (!pending) return;

    axios
      .put(CHART_SETTINGS_URL, pending)
      .catch(() => showSaveErrorRef.current());
  }, []);

  useEffect(() => {
    let isActual = true;

    canSaveRef.current = false;
    setIsLoaded(false);

    const apply = (saved, canSave) => {
      const next = {
        ...parseChartSettings(saved),
        [TOOLBAR_POSITION_FIELD]: settingsRef.current[TOOLBAR_POSITION_FIELD],
      };

      settingsRef.current = next;
      canSaveRef.current = canSave;
      setSettings(next);
      setIsLoaded(true);
    };

    if (!token) {
      apply(null, false);
      return undefined;
    }

    axios
      .get(CHART_SETTINGS_URL)
      .then(({ data }) => {
        if (isActual) apply(data.data, true);
      })
      .catch(() => {
        if (isActual) apply(null, false);
      });

    return () => {
      isActual = false;
    };
  }, [token]);

  // A change waiting for the delay still gets saved when the chart goes away
  useEffect(() => sendPending, [sendPending]);

  // A normal request doesn't survive closing the page, keepalive does
  useEffect(() => {
    const handlePageHide = () => {
      const pending = takePending();

      if (!pending) return;

      fetch(`${config.api_url}${CHART_SETTINGS_URL}`, {
        method: "PUT",
        keepalive: true,
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(pending),
      }).catch(() => {});
    };

    window.addEventListener("pagehide", handlePageHide);

    return () => window.removeEventListener("pagehide", handlePageHide);
  }, []);

  const updateSettings = useCallback(
    (partial) => {
      const changedFields = Object.keys(partial).filter(
        (field) => !isSameValue(settingsRef.current[field], partial[field]),
      );

      if (!changedFields.length) return;

      const next = { ...settingsRef.current, ...partial };

      settingsRef.current = next;
      setSettings(next);

      const fieldsToSend = changedFields.filter(
        (field) => field !== TOOLBAR_POSITION_FIELD,
      );

      if (changedFields.includes(TOOLBAR_POSITION_FIELD)) {
        saveDrawingToolbarPosition(next[TOOLBAR_POSITION_FIELD]);
      }

      if (!canSaveRef.current || !fieldsToSend.length) return;

      fieldsToSend.forEach((field) => {
        pendingRef.current[field] = next[field];
      });
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(sendPending, SAVE_DELAY_MS);
    },
    [sendPending],
  );

  return {
    settings,
    effectiveSettings: getEffectiveChartSettings(settings),
    updateSettings,
    isLoaded,
  };
};

export default useChartSettings;
